import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import {
  faQuestionCircle,
  faEllipsisH,
} from '@fortawesome/pro-regular-svg-icons'
import {
  faCheck,
  faClipboardList,
  faSparkle,
} from '@fortawesome/pro-solid-svg-icons'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'

// TODO:  Move to v2-components taxes
import { Tooltip as TaskTooltip } from '@/features/taxes/quarterly/components/Tooltip'
import { DeadlineLabel } from '@/features/taxes/quarterly/components/DeadlineLabel'

import { useAnalyticsTrack } from '@/features/amplitude'
import { markUserActionItemCompleteIfExists } from '../lib/service'

import {
  updateUserActionItem,
  UserActionItem,
} from './lib/userActionItems.slice'
import {
  PostOnboardingTaskIdents,
  templateDescription,
} from '@/features/onboarding/config'
import { UserActionListItemExtras } from './UserActionListItemExtras'
import { ACTION_ITEM_TYPE } from './UserActionItemTabPane'

import { mtc, TextColors, TypographyStyles } from '@/styles'
import { cardStyles, dateStyles } from './action-item.styles'
import { Button, Card, CardColors, Dropdown, Icon, Text, Tooltip } from '@/ui'

interface Props {
  dropdownOptions?: {
    key: string
    text: string
    onClick?: (id: number, description?: string) => void
  }[]
  userActionItem: UserActionItem
  selectedTabPane: ACTION_ITEM_TYPE
}

const isActionItemOverdue = ({
  actionItem: { endsAt },
  endsAt: userActionEndsAt,
}: UserActionItem) => {
  if (!endsAt && !userActionEndsAt) {
    return false
  }

  const dueDate = moment.utc(userActionEndsAt || endsAt).endOf('D')
  return moment().isAfter(dueDate, 'D')
}

const getDateState = (
  userActionItem: UserActionItem,
  selectedTabPane: ACTION_ITEM_TYPE
): 'completed' | 'upcoming' | 'overdue' | 'warning' => {
  if (selectedTabPane === ACTION_ITEM_TYPE.COMPLETED) {
    return 'completed'
  }

  if (selectedTabPane === ACTION_ITEM_TYPE.UPCOMING) {
    return 'upcoming'
  }

  return isActionItemOverdue(userActionItem) ? 'overdue' : 'warning'
}

const getDeadlineColumn = ({
  userActionItem,
  selectedTabPane,
}: {
  userActionItem: UserActionItem
  selectedTabPane: ACTION_ITEM_TYPE
}) => {
  const {
    actionItem: { endsAt },
    completedAt,
    endsAt: userActionEndsAt,
  } = userActionItem

  const dateState = getDateState(userActionItem, selectedTabPane)

  if (
    (endsAt || userActionEndsAt) &&
    selectedTabPane !== ACTION_ITEM_TYPE.COMPLETED
  ) {
    return (
      <div className="shrink-0 md:pl-4 md:text-right">
        <DeadlineLabel
          className={mtc(dateStyles({ state: dateState }))}
          text={
            selectedTabPane === ACTION_ITEM_TYPE.ACTIVE &&
            isActionItemOverdue(userActionItem)
              ? 'Overdue' + ' '
              : 'Due' + ' '
          }
          prominentText={moment
            .utc(userActionEndsAt || endsAt)
            .format(DATE_FORMATS.DISPLAY_SHORT)}
        />
      </div>
    )
  }

  if (completedAt && selectedTabPane === ACTION_ITEM_TYPE.COMPLETED) {
    return (
      <div className="shrink-0 md:pl-4 md:text-right">
        <DeadlineLabel
          className={mtc(dateStyles({ state: dateState }))}
          text="Completed"
          prominentText={moment(completedAt).format(DATE_FORMATS.DISPLAY_SHORT)}
        />
      </div>
    )
  }

  return null
}

export const UserActionItemListItem = ({
  dropdownOptions,
  userActionItem,
  selectedTabPane,
}: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [extraModalOpen, setExtraModalOpen] = useState(false)
  const track = useAnalyticsTrack()
  const isOverdue = isActionItemOverdue(userActionItem)
  const markUserActionItemRead = useCallback(
    (id: number) =>
      updateUserActionItem(id, { id, readAt: moment().toJSON() })(dispatch),
    [dispatch]
  )

  const {
    actionItem: {
      actionItemCategory,
      actionLink,
      description,
      tooltip,
      identifier,
      canMarkComplete,
      id: actionItemId,
    },
    id,
    readAt,
    templateValue,
  } = userActionItem

  const itemDescription = useMemo(
    () => templateDescription(description, templateValue),
    [description, templateValue]
  )

  const tooltipTitle = useMemo(
    () => templateDescription(tooltip?.title ?? '', templateValue),
    [tooltip?.title, templateValue]
  )

  const deadlineColumn = useMemo(
    () => getDeadlineColumn({ userActionItem, selectedTabPane }),
    [selectedTabPane, userActionItem]
  )

  const handleCardClick = async (event: React.MouseEvent) => {
    // Only handle clicks for active items
    if (selectedTabPane !== ACTION_ITEM_TYPE.ACTIVE) {
      return
    }

    // Prevent default <a> navigation
    event.preventDefault()

    // Perform save
    await markUserActionItemRead(id)

    // Special case for get to know link.  As soon as it's clicked it should mark as complete
    if (identifier === PostOnboardingTaskIdents.GET_TO_KNOW_HEARD) {
      markUserActionItemCompleteIfExists(
        PostOnboardingTaskIdents.GET_TO_KNOW_HEARD,
        (event, properties) => track(event, properties)
      )
    }

    track('clicked todo', {
      todo_description: description,
      todo_id: actionItemId,
    })

    if (actionLink) {
      if (
        new URL(document.baseURI).origin ===
        new URL(actionLink, document.baseURI).origin
      ) {
        // If `actionLink` is relative, navigate to path
        navigate(actionLink)
      } else {
        // Otherwise, open a new window with external link
        window.open(actionLink, '_blank', 'noreferrer')
      }
    } else {
      setExtraModalOpen(true)
    }
  }

  const cardBackgroundColor = useMemo(() => {
    if (isOverdue) {
      return CardColors.DANGER
    }

    if (
      selectedTabPane === ACTION_ITEM_TYPE.COMPLETED ||
      selectedTabPane === ACTION_ITEM_TYPE.UPCOMING
    ) {
      return CardColors.LEVEL_1
    }

    if (!readAt) {
      return CardColors.WARNING
    }
    return CardColors.LEVEL_1
  }, [selectedTabPane, readAt, isOverdue])

  return (
    <>
      <Card
        className={mtc(cardStyles())}
        childrenClassName="pt-3 pb-3 pr-4 pl-4"
        onClick={handleCardClick}
        color={cardBackgroundColor}
      >
        <div className="flex flex-col items-start md:flex-row md:items-center">
          <div className="hidden md:block">
            <Icon
              backgroundColor={isOverdue ? 'warning' : 'black'}
              size="sm"
              color="text-neutral-0"
              backgroundStyle="rounded"
              className="w-4 h-4"
              icon={
                selectedTabPane === ACTION_ITEM_TYPE.COMPLETED
                  ? faCheck
                  : faClipboardList
              }
            />
          </div>

          <div className="min-w-0 w-full md:ml-4 md:flex-1">
            <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
              {actionItemCategory?.title}
            </Text>

            <Tooltip>
              <Tooltip.Trigger asChild>
                <div className="flex items-center gap-2 min-w-0 h-7">
                  {selectedTabPane === ACTION_ITEM_TYPE.ACTIVE && !readAt && (
                    <Icon
                      icon={faSparkle}
                      className="shrink-0 w-3 h-3"
                      color="text-green-50"
                    />
                  )}

                  <Text variant={TypographyStyles.H3} className="truncate">
                    {itemDescription}
                  </Text>
                </div>
              </Tooltip.Trigger>
              <Tooltip.Content>
                <Text variant={TypographyStyles.BODY_SM}>
                  {itemDescription}
                </Text>
              </Tooltip.Content>
            </Tooltip>
          </div>

          {deadlineColumn}

          {tooltip?.body && (
            <span className="hidden md:inline-flex">
              <TaskTooltip
                className="mx-2.5"
                labelComponent={
                  <Icon
                    icon={faQuestionCircle}
                    className="w-4 h-4 text-gray-500"
                  />
                }
                popup={{
                  title: tooltipTitle,
                  body: tooltip?.body || '',
                  link: tooltip?.link || '',
                }}
              />
            </span>
          )}

          <div
            className={`hidden lg:block w-8 ${selectedTabPane === ACTION_ITEM_TYPE.UPCOMING ? 'invisible' : ''}`}
          >
            {canMarkComplete && (
              <Dropdown>
                <Dropdown.Trigger asChild>
                  <Button
                    variant="discreet"
                    size="square"
                    iconRight={faEllipsisH}
                    className="p-0 bg-transparent border-none"
                  />
                </Dropdown.Trigger>

                <Dropdown.Content align="end" className="p-1">
                  {dropdownOptions?.map((option) => (
                    <Dropdown.Item
                      key={`UpcomingActionItem__${id}_${option.key}`}
                      onClick={() => option.onClick?.(id, description)}
                    >
                      <Text className="text-left">{option.text}</Text>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Content>
              </Dropdown>
            )}
          </div>
        </div>
      </Card>

      <UserActionListItemExtras
        userActionItemId={userActionItem.id}
        open={extraModalOpen}
        onClose={() => setExtraModalOpen(false)}
      />
    </>
  )
}
